import { useState, useEffect, useCallback, useRef } from 'react';
import { Home, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';

const PtzControls = ({ integrationId, cameraGuid, integrationType }) => {
  const [visible, setVisible] = useState(false);
  const [isPtz, setIsPtz] = useState(false);
  const [presets, setPresets] = useState([]);
  const [showPresets, setShowPresets] = useState(false);
  const [speed, setSpeed] = useState(50);
  const [activeDir, setActiveDir] = useState(null);
  const stopTimerRef = useRef(null);

  // Only supports Genetec for now
  const isGenetec = integrationType === 'GENETEC_VMS';
  const baseUrl = isGenetec ? `/api/vms/genetec/${integrationId}/ptz/${encodeURIComponent(cameraGuid)}` : null;

  // Check PTZ capabilities on mount
  useEffect(() => {
    if (!baseUrl) return;
    fetchWithTenant(`${baseUrl}/capabilities`)
      .then(r => r.json())
      .then(data => {
        if (data.isPtz) {
          setIsPtz(true);
          // Load presets
          fetchWithTenant(`${baseUrl}/presets`)
            .then(r => r.json())
            .then(d => setPresets(d.presets || []))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [baseUrl]);

  const sendMove = useCallback((direction) => {
    if (!baseUrl) return;
    setActiveDir(direction);
    fetchWithTenant(`${baseUrl}/move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ direction, speed }),
    }).catch(err => console.error('[PTZ] Move error:', err));
  }, [baseUrl, speed]);

  const sendStop = useCallback(() => {
    if (!baseUrl) return;
    setActiveDir(null);
    fetchWithTenant(`${baseUrl}/stop`, { method: 'POST' })
      .catch(err => console.error('[PTZ] Stop error:', err));
  }, [baseUrl]);

  const sendHome = useCallback(() => {
    if (!baseUrl) return;
    fetchWithTenant(`${baseUrl}/home`, { method: 'POST' })
      .catch(err => console.error('[PTZ] Home error:', err));
  }, [baseUrl]);

  const sendPreset = useCallback((presetId) => {
    if (!baseUrl) return;
    setShowPresets(false);
    fetchWithTenant(`${baseUrl}/preset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ presetId }),
    }).catch(err => console.error('[PTZ] Preset error:', err));
  }, [baseUrl]);

  const handlePointerDown = useCallback((direction) => {
    sendMove(direction);
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
  }, [sendMove]);

  const handlePointerUp = useCallback(() => {
    // Small delay before stopping to allow smooth movement
    stopTimerRef.current = setTimeout(() => sendStop(), 100);
  }, [sendStop]);

  // Keyboard controls when PTZ panel is visible
  useEffect(() => {
    if (!visible || !isPtz) return;
    const keyMap = {
      ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
      '+': 'zoom-in', '=': 'zoom-in', '-': 'zoom-out',
    };
    const handleKeyDown = (e) => {
      const dir = keyMap[e.key];
      if (dir) { e.preventDefault(); sendMove(dir); }
    };
    const handleKeyUp = (e) => {
      const dir = keyMap[e.key];
      if (dir) { e.preventDefault(); sendStop(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [visible, isPtz, sendMove, sendStop]);

  if (!isPtz) return null;

  // D-pad button helper
  const DPadBtn = ({ dir, children, className }) => (
    <button
      className={`ptz-dpad-btn ${className || ''} ${activeDir === dir ? 'active' : ''}`}
      onPointerDown={() => handlePointerDown(dir)}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      {children}
    </button>
  );

  return (
    <>
      {/* Toggle button */}
      <button
        className={`ptz-toggle-btn ${visible ? 'active' : ''}`}
        onClick={() => setVisible(!visible)}
        title="PTZ Controls"
      >
        <Maximize2 size={16} />
        <span>PTZ</span>
      </button>

      {/* PTZ Control Panel */}
      {visible && (
        <div className="ptz-panel" onClick={(e) => e.stopPropagation()}>
          {/* D-Pad */}
          <div className="ptz-dpad">
            <DPadBtn dir="up-left" className="ptz-corner tl">
              <svg width="12" height="12" viewBox="0 0 12 12"><path d="M10 10L2 2M2 2v6M2 2h6" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
            </DPadBtn>
            <DPadBtn dir="up" className="ptz-cardinal top">
              <svg width="14" height="14" viewBox="0 0 24 24"><path d="M12 5l-6 6h12z" fill="currentColor"/></svg>
            </DPadBtn>
            <DPadBtn dir="up-right" className="ptz-corner tr">
              <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 10L10 2M10 2v6M10 2H4" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
            </DPadBtn>
            <DPadBtn dir="left" className="ptz-cardinal left">
              <svg width="14" height="14" viewBox="0 0 24 24"><path d="M5 12l6-6v12z" fill="currentColor"/></svg>
            </DPadBtn>
            <button className="ptz-dpad-btn ptz-center" onClick={sendHome} title="Home">
              <Home size={14} />
            </button>
            <DPadBtn dir="right" className="ptz-cardinal right">
              <svg width="14" height="14" viewBox="0 0 24 24"><path d="M19 12l-6-6v12z" fill="currentColor"/></svg>
            </DPadBtn>
            <DPadBtn dir="down-left" className="ptz-corner bl">
              <svg width="12" height="12" viewBox="0 0 12 12"><path d="M10 2L2 10M2 10V4M2 10h6" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
            </DPadBtn>
            <DPadBtn dir="down" className="ptz-cardinal bottom">
              <svg width="14" height="14" viewBox="0 0 24 24"><path d="M12 19l6-6H6z" fill="currentColor"/></svg>
            </DPadBtn>
            <DPadBtn dir="down-right" className="ptz-corner br">
              <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 2l8 8M10 10V4M10 10H4" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
            </DPadBtn>
          </div>

          {/* Zoom */}
          <div className="ptz-zoom-row">
            <button
              className={`ptz-zoom-btn ${activeDir === 'zoom-out' ? 'active' : ''}`}
              onPointerDown={() => handlePointerDown('zoom-out')}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            >
              <ZoomOut size={16} />
            </button>
            <input
              type="range"
              className="ptz-speed-slider"
              min="10"
              max="100"
              step="10"
              value={speed}
              onChange={(e) => setSpeed(parseInt(e.target.value))}
              title={`Speed: ${speed}%`}
            />
            <button
              className={`ptz-zoom-btn ${activeDir === 'zoom-in' ? 'active' : ''}`}
              onPointerDown={() => handlePointerDown('zoom-in')}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            >
              <ZoomIn size={16} />
            </button>
          </div>

          {/* Presets */}
          {presets.length > 0 && (
            <div className="ptz-presets">
              <button className="ptz-presets-toggle" onClick={() => setShowPresets(!showPresets)}>
                Presets ({presets.length})
              </button>
              {showPresets && (
                <div className="ptz-presets-list">
                  {presets.map(p => (
                    <button key={p.id} className="ptz-preset-btn" onClick={() => sendPreset(p.id)}>
                      {p.name || `Preset ${p.id}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PtzControls;
